import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  BookLock,
  CalendarClock,
  HeartHandshake,
  LineChart,
  Paperclip,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { RelationshipCheck } from "@/components/abuse-journal/RelationshipCheck";
import { withMemberGate } from "@/lib/with-member-gate";
import { ConfirmDelete } from "@/components/abuse-journal/ConfirmDelete";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { QuickExit } from "@/components/abuse-journal/QuickExit";
import { IncidentEditor } from "@/components/abuse-journal/IncidentEditor";
import { TimelinePanel } from "@/components/abuse-journal/TimelinePanel";
import { PatternsPanel } from "@/components/abuse-journal/PatternsPanel";
import { EvidencePanel } from "@/components/abuse-journal/EvidencePanel";
import { SafetyPanel } from "@/components/abuse-journal/SafetyPanel";
import { useAbuseJournal } from "@/components/abuse-journal/useAbuseJournal";
import { JournalGuide } from "@/components/JournalGuide";
import {
  EMPTY_FILTERS,
  JournalSearch,
  filterEntries,
  type JournalFilters,
} from "@/components/abuse-journal/JournalSearch";
import {
  entryLabel,
  entryTime,
  formatDate,
  formatDateTime,
  type AbuseEntry,
} from "@/lib/abuse-journal";

export const Route = createFileRoute("/abuse-journal")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search['tab'] === "string" ? (search['tab'] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Abuse Recovery & Evidence Journal | Recovery Trauma™" },
      {
        name: "description",
        content:
          "A private, trauma-informed space to record what happened, notice patterns over time and keep your own records safely — visible only to you.",
      },
      { property: "og:title", content: "Abuse Recovery & Evidence Journal | Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Privately document incidents, keep supporting material together and export your own records. Member-only and visible only to you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: withMemberGate(AbuseJournalPage, "Abuse Recovery & Evidence Journal"),
});

type Tab = "journal" | "check" | "timeline" | "patterns" | "evidence" | "safety";

const TABS: { id: Tab; label: string; icon: typeof BookLock }[] = [
  { id: "journal", label: "My journal", icon: BookLock },
  { id: "check", label: "Relationship check", icon: HeartHandshake },
  { id: "timeline", label: "Timeline", icon: CalendarClock },
  { id: "patterns", label: "Patterns", icon: LineChart },
  { id: "evidence", label: "Evidence", icon: Paperclip },
  { id: "safety", label: "Safety & privacy", icon: ShieldCheck },
];

const TAB_IDS: Tab[] = TABS.map((t) => t.id);

function AbuseJournalPage() {
  const journal = useAbuseJournal();
  const { tab: tabParam } = Route.useSearch();
  const [tab, setTab] = useState<Tab>(
    TAB_IDS.includes(tabParam as Tab) ? (tabParam as Tab) : "journal",
  );
  const [editing, setEditing] = useState<AbuseEntry | null | "new">(null);
  const [filters, setFilters] = useState<JournalFilters>(EMPTY_FILTERS);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const ordered = useMemo(
    () => [...journal.entries].sort((a, b) => entryTime(b) - entryTime(a)),
    [journal.entries],
  );

  const filtered = useMemo(() => filterEntries(ordered, filters), [ordered, filters]);

  return (
    <main className="mx-auto w-full max-w-4xl px-4 pb-24 pt-6 sm:px-6">
      <div
        className="mb-5 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-6"
        style={{
          backgroundImage:
            "linear-gradient(150deg, var(--qa-teal-tint) 0%, var(--qa-mint-tint) 50%, var(--card) 100%)",
        }}
      >
        <div className="min-w-0">
          <p
            className="inline-flex rounded-full bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--qa-teal-ink)" }}
          >
            Private space
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-foreground sm:text-3xl">
            Abuse Recovery &amp; Evidence Journal
          </h1>
        </div>
        <QuickExit />
      </div>

      <JournalGuide journal="abuse" className="mb-5" />

      <p className="text-sm leading-relaxed text-foreground/80">
        A quiet place to write down what happened, in your own words and at your own pace.
        You don&apos;t have to name anything, explain anything, or finish anything. Whatever
        you record stays private to you.
      </p>

      <PrivacyNotice className="mt-4">
        Only you can see this journal. Nothing here is shared with other members, used for
        analytics, or shown anywhere else on Recovery Trauma™.
      </PrivacyNotice>

      <p className="mt-3 text-xs leading-relaxed text-foreground/60">
        Recovery Trauma™ is not an emergency service and does not give legal, medical or
        therapeutic advice. If you are in immediate danger, contact your local emergency
        services.
      </p>

      {editing ? (
        <div className="mt-8">
          <IncidentEditor
            entry={editing === "new" ? null : editing}
            saving={journal.isSaving}
            onCancel={() => setEditing(null)}
            onSave={async (draft) => {
              const res = (await journal.saveEntry(draft)) as { entry?: AbuseEntry } | undefined;
              return res?.entry;
            }}
          />
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="rounded-2xl border border-border bg-card/60 p-5 text-start transition hover:border-[var(--gold-deep)]/50"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                <Plus className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden /> Record an
                Incident
              </span>
              <span className="mt-2 block text-xs leading-relaxed text-foreground/70">
                Write down what happened, in your own words and at your own pace.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTab("check")}
              className="rounded-2xl border border-border bg-card/60 p-5 text-start transition hover:border-[var(--gold-deep)]/50"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                <HeartHandshake className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden />{" "}
                Check My Relationship
              </span>
              <span className="mt-2 block text-xs leading-relaxed text-foreground/70">
                Not sure whether some of the behaviours in your relationship are concerning?
                Take a short, private relationship safety check.
              </span>
            </button>
          </div>

          <nav className="mt-6 flex gap-2 overflow-x-auto pb-1" aria-label="Journal sections">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  aria-current={active ? "page" : undefined}
                  className={`inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border px-4 text-sm transition ${
                    active
                      ? "border-transparent bg-foreground text-background"
                      : "border-border bg-background/60 text-foreground/80"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div className="mt-6">
            {tab === "journal" && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setEditing("new")}
                    className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-background transition hover:opacity-90"
                  >
                    <Plus className="h-4 w-4" aria-hidden /> Record something
                  </button>
                </div>

                <JournalSearch
                  entries={ordered}
                  value={filters}
                  onChange={setFilters}
                  resultCount={filtered.length}
                />

                {filtered.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card/40 px-4 py-3">
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground/75">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length > 0 && selectedIds.length === filtered.length
                        }
                        onChange={(ev) =>
                          setSelectedIds(ev.target.checked ? filtered.map((e) => e.id) : [])
                        }
                        className="h-4 w-4 rounded border-border"
                      />
                      Select all shown
                    </label>
                    <p className="text-xs text-foreground/60">
                      {selectedIds.length
                        ? `${selectedIds.length} selected`
                        : "Tick entries to remove several at once."}
                    </p>
                    {selectedIds.length > 0 && (
                      <ConfirmDelete
                        title="Delete selected entries?"
                        description={`You're about to permanently delete ${selectedIds.length} ${
                          selectedIds.length === 1 ? "entry" : "entries"
                        } and any files attached to them.`}
                        confirmLabel="Delete selected"
                        onConfirm={async () => {
                          for (const id of selectedIds) await journal.deleteEntry(id);
                          setSelectedIds([]);
                        }}
                        trigger={
                          <button
                            type="button"
                            className="ms-auto inline-flex min-h-[40px] cursor-pointer items-center gap-1.5 rounded-full border border-destructive/40 px-4 text-xs text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete selected
                          </button>
                        }
                      />
                    )}
                  </div>
                )}




                {journal.isLoading ? (
                  <p className="text-sm text-foreground/65">Opening your journal…</p>
                ) : filtered.length === 0 ? (
                  <p className="rounded-2xl border border-border bg-card/60 p-6 text-sm leading-relaxed text-foreground/75">
                    {ordered.length === 0
                      ? "Nothing here yet. When you're ready — and only if you want to — you can record a first entry. There's no right amount to write."
                      : "No entries match that search."}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {filtered.map((e) => (
                      <li key={e.id} className="rounded-2xl border border-border bg-card/60 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-3">
                            <input
                              type="checkbox"
                              aria-label={`Select ${entryLabel(e)}`}
                              checked={selectedIds.includes(e.id)}
                              onChange={() =>
                                setSelectedIds((s) =>
                                  s.includes(e.id)
                                    ? s.filter((x) => x !== e.id)
                                    : [...s, e.id],
                                )
                              }
                              className="mt-1 h-4 w-4 shrink-0 rounded border-border"
                            />
                            <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">
                              {e.date_unknown || !e.occurred_on
                                ? "Date not recorded"
                                : formatDate(e.occurred_on)}
                              {e.occurred_time ? ` · ${e.occurred_time}` : ""}
                            </p>
                            <p className="mt-0.5 text-xs text-foreground/60">
                              Created {formatDateTime(e.created_at)}
                              {e.updated_at && e.updated_at !== e.created_at
                                ? ` · Last edited ${formatDateTime(e.updated_at)}`
                                : ""}
                              {e.location ? ` · ${e.location}` : ""}
                            </p>

                            {!!(e.behaviours ?? []).length && (
                              <p className="mt-2 flex flex-wrap gap-1.5">
                                {e.behaviours.map((b) => (
                                  <span
                                    key={b}
                                    className="rounded-full border border-border px-2.5 py-0.5 text-[11px] text-foreground/75"
                                  >
                                    {b}
                                  </span>
                                ))}
                              </p>
                            )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setEditing(e)}
                              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border px-3 text-xs"
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden /> Open
                            </button>
                            <ConfirmDelete
                              title="Delete this entry?"
                              description={`"${entryLabel(e)}" and any files attached to it will be permanently removed.`}
                              onConfirm={() => journal.deleteEntry(e.id)}
                              trigger={
                                <button
                                  type="button"
                                  aria-label={`Delete ${entryLabel(e)}`}
                                  className="inline-flex min-h-[40px] cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 text-xs text-foreground/70"
                                >
                                  <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete
                                </button>
                              }
                            />
                          </div>
                        </div>
                        {e.what_happened && (
                          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground/80">
                            {e.what_happened}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === "check" && (
              <RelationshipCheck
                saving={journal.isSaving}
                onRecordIncident={() => setEditing("new")}
                onGoToJournal={() => setTab("journal")}
                onViewSafety={() => setTab("safety")}
                onSave={async (text) => {
                  await journal.saveEntry({
                    occurred_on: new Date().toISOString().slice(0, 10),
                    behaviours: ["Relationship Safety Check"],
                    what_happened: text,
                  });
                }}
              />
            )}

            {tab === "timeline" && (
              <TimelinePanel entries={journal.entries} onOpen={(e) => setEditing(e)} />
            )}

            {tab === "patterns" && <PatternsPanel entries={journal.entries} />}

            {tab === "evidence" && (
              <EvidencePanel
                entries={ordered}
                attachments={journal.attachments}
                onRecord={journal.recordAttachment}
                onUpdate={journal.updateAttachment}
                onDelete={journal.deleteAttachment}
              />
            )}

            {tab === "safety" && <SafetyPanel onDeleteAll={journal.deleteEverything} />}
          </div>
        </>
      )}
    </main>
  );
}
